import {fromPairs, isUndefined} from 'lodash';
import {DateTime} from 'luxon';
import {createSelector, OutputSelector} from 'reselect';

import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {
  analyticsReportConfigs,
  AnalyticsReportsEnum,
  getMetricsFromReportElementConfig,
  ReportFilterOptionsEnum,
} from '../../../../analytics/helpers/analyticsReportHelpers';
import {
  AnalyticsGraphMetric,
  AnalyticsHeatMapMetric,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricError,
  AnalyticsMetricErrorTypesEnum,
  AnalyticsMetricsDictionary,
  AnalyticsMetricsKeys,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetric,
  AnalyticsTableMetricsEnum,
  isAnalyticsMetricKey,
} from '../../../../models/api/analyticsMetricsModel';
import {AnalyticsSelectedMetricsModel} from '../../../../models/api/analyticsSavedViewModel';
import {AppState} from '../../../appState';
import {getAnalyticsPanel} from '../analyticsPanelSelectors';
import {getAnalyticsReportType} from '../parameters/analyticsPanelParametersSelectors';
import {AnalyticsReportJobPrioritiesEnum} from './analyticsPanelReportState';

export const getAnalyticsReport = createSelector(getAnalyticsPanel, ({report}) => report);

export const getAnalyticsReportJobs = createSelector(getAnalyticsReport, ({jobs}) => jobs);

export const getAnalyticsReportHasStarted = createSelector(getAnalyticsReportJobs, (jobs) =>
  jobs.some((job) => job.hasStarted),
);

export const getAnalyticsReportProgress = createSelector(getAnalyticsReportJobs, (jobs) => {
  // Only keep jobs that have no error.
  const progresses = jobs.filter(({error}) => !error).map(({progress}) => progress);

  // Temporary calculating an average.
  return progresses.length ? progresses.reduce((acc, curr) => acc + curr, 0) / progresses.length : 0;
});

// Temporary set the loading indicator is one of the primary job is still loading or if we don't have any jobs yet.
export const getAnalyticsReportIsLoading = createSelector(
  getAnalyticsReportJobs,
  (jobs) =>
    !jobs.length ||
    jobs
      .filter(({priority}) => priority === AnalyticsReportJobPrioritiesEnum.PRIMARY)
      .some(({isLoading}) => Boolean(isLoading)),
);

export const getAnalyticsSecondaryMetricIsLoading = createSelector(
  getAnalyticsReportJobs,
  (jobs) => (metric: AnalyticsMetricsKeys) =>
    !jobs.length ||
    jobs
      .filter(({priority}) => priority === AnalyticsReportJobPrioritiesEnum.SECONDARY)
      .filter((job) => job.metrics.includes(metric))
      .some(({isLoading}) => Boolean(isLoading)),
);

// Temporary getting the max endTime.
export const getAnalyticsReportLastUpdated = createSelector(getAnalyticsReportJobs, (jobs) => {
  const endTimes = jobs
    .map(({endTime}) => endTime)
    .filter((endTime): endTime is DateTime => !isUndefined(endTime));

  if (endTimes.length === 0) {
    return undefined;
  }

  return DateTime.max(...endTimes);
});

export const getAnalyticsReportErrors = createSelector(getAnalyticsReportJobs, (jobs) =>
  jobs.map((job) => job.error).filter((error): error is string => !isUndefined(error)),
);

export const getAnalyticsSelectedMetrics = createSelector(
  getAnalyticsReport,
  ({options}) => options[ReportFilterOptionsEnum.SELECTED_METRICS] ?? {},
);

export const getAnalyticsBucketBoundaries = createSelector(
  getAnalyticsReport,
  ({options}) => options[ReportFilterOptionsEnum.BUCKET_BOUNDARIES] ?? {},
);

export const getAnalyticsReportOptions = createSelector(getAnalyticsReport, ({options}) => options);

/**
 * For the selected report type, will return the list of displayed metrics to be fetched first, and hidden metrics.
 * This will return the list of metrics set by default in the config, or build a new array based on selectedMetrics.
 */
export const getAnalyticsMetricsToFetch = createSelector(
  [getAnalyticsReportType, getAnalyticsSelectedMetrics],
  getAnalyticsMetricsToFetchForReportType,
);

export const getAllAnalyticsMetricsToFetch = createSelector(getAnalyticsSelectedMetrics, (selectedMetrics) =>
  fromPairs(
    collectEnumValues(AnalyticsReportsEnum).map((reportType) => [
      reportType,
      getAnalyticsMetricsToFetchForReportType(reportType, selectedMetrics),
    ]),
  ),
);

export const getAnalyticsMetrics = createSelector(getAnalyticsReport, ({metrics}) => metrics);
export const getTimedOutAnalyticsMetrics = createSelector(
  getAnalyticsReport,
  ({timedOutMetrics}) => timedOutMetrics,
);

type StateMetric<T> =
  | (T extends AnalyticsScalarMetricsEnum
      ? AnalyticsScalarMetric
      : T extends AnalyticsTableMetricsEnum
      ? AnalyticsTableMetric
      : T extends AnalyticsHeatMapMetricsEnum
      ? AnalyticsHeatMapMetric
      : AnalyticsGraphMetric)
  | undefined;

export function makeGetAnalyticsMetric<T extends AnalyticsMetricsKeys>(
  metricName?: T,
): OutputSelector<
  AppState,
  [StateMetric<T>, AnalyticsMetricError | undefined],
  (
    metrics: AnalyticsMetricsDictionary,
    timedOutMetrics: AnalyticsMetricsKeys[],
  ) => [StateMetric<T>, AnalyticsMetricError | undefined]
> {
  return createSelector([getAnalyticsMetrics, getTimedOutAnalyticsMetrics], (metrics, timedOutMetrics) => {
    if (!metricName) {
      return [undefined, undefined];
    }

    const metricError = timedOutMetrics.includes(metricName)
      ? {id: metricName, errorType: AnalyticsMetricErrorTypesEnum.TIMED_OUT}
      : undefined;
    const metric = metrics[metricName as keyof AnalyticsMetricsDictionary];

    return [metric, metricError];
  });
}

/*
 * Helpers.
 */

function getAnalyticsMetricsToFetchForReportType(
  reportType: AnalyticsReportsEnum,
  selectedMetrics: AnalyticsSelectedMetricsModel,
) {
  const reportConfig = analyticsReportConfigs[reportType];

  const metricsToFetchFirst = new Set<AnalyticsMetricsKeys>(); // Using Sets to make sure we don't fetch the same metric twice.
  const metricsToFetchSecond = new Set<AnalyticsMetricsKeys>();

  // For each report element of this report:
  reportConfig.reportElements.forEach((reportConfigElement) => {
    // 1. Get the default and non-default metrics from the config.
    const {displayedMetrics: visibleConfigMetrics, availableMetrics: allConfigMetrics = []} =
      getMetricsFromReportElementConfig<AnalyticsMetricsKeys>(reportConfigElement);
    const hiddenConfigMetrics = allConfigMetrics.filter((metric) => !visibleConfigMetrics.includes(metric));

    // 2. Get the potential selected metrics from the store.
    const potentialSelectedMetrics = selectedMetrics[reportConfigElement.id];

    // 3. Excluding potential stuff that is not an AnalyticsMetricsKeys.
    const selectedMetricsForReportElement = (potentialSelectedMetrics || []).filter(isAnalyticsMetricKey);

    // 4. If we have at least one selected metrics for this element that matches an existing metric in the config:
    const matchingMetrics = selectedMetricsForReportElement.filter((selectedMetric) =>
      allConfigMetrics.includes(selectedMetric),
    );
    if (matchingMetrics.length) {
      // Add selected metrics to the list of metrics to fetch first. The other metrics for this element can be fetched second.
      matchingMetrics.forEach((metric) => metricsToFetchFirst.add(metric));
      allConfigMetrics
        .filter((m) => !matchingMetrics.includes(m))
        .forEach(metricsToFetchSecond.add, metricsToFetchSecond);
    } else {
      // Else, use report default metrics from config.
      visibleConfigMetrics.forEach((metric) => metricsToFetchFirst.add(metric));
      hiddenConfigMetrics.forEach((metric) => metricsToFetchSecond.add(metric));
    }
  });

  return {
    displayedMetrics: Array.from(metricsToFetchFirst),
    hiddenMetrics: Array.from(metricsToFetchSecond),
  };
}
